import { AnalyticsEvent, AnalyticsProvider, VideoEvent, WatchTimeEvent } from '../types';
import { getEnvConfig } from '../../../config/env';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export class GoogleAnalyticsProvider implements AnalyticsProvider {
  private initialized = false;
  private readonly measurementId: string;

  constructor() {
    this.measurementId = getEnvConfig().providers.googleAnalytics.measurementId;
    if (!this.measurementId) {
      console.warn('Google Analytics Measurement ID is not configured');
    }
  }

  async initialize(): Promise<void> {
    if (this.initialized || !this.measurementId) return;

    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    document.head.appendChild(script);

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };

    // Configure GA
    window.gtag('js', new Date());
    window.gtag('config', this.measurementId, {
      send_page_view: false, // We'll handle page views manually
      debug_mode: getEnvConfig().environment === 'development'
    });

    this.initialized = true;
  }

  private ensureInitialized() {
    if (!this.initialized) {
      throw new Error('Google Analytics provider must be initialized before use');
    }
  }

  trackEvent(event: AnalyticsEvent): void {
    this.ensureInitialized();
    
    window.gtag('event', event.action, {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
    });
  }

  trackPageView(path: string): void {
    this.ensureInitialized();

    window.gtag('event', 'page_view', {
      page_path: path,
    });
  }

  trackVideoEvent(event: VideoEvent): void {
    this.ensureInitialized();

    window.gtag('event', event.action, {
      event_category: 'Video',
      event_label: event.label || event.videoId,
      video_id: event.videoId,
      video_timestamp: event.timestamp,
      video_duration: event.duration,
      video_current_time: event.currentTime || 0,
      video_percent: event.duration ? ((event.currentTime || 0) / event.duration) * 100 : 0,
    });
  }

  trackWatchTime(event: WatchTimeEvent): void {
    this.ensureInitialized();

    window.gtag('event', 'watch_time', {
      event_category: 'Video',
      event_label: event.videoId,
      video_id: event.videoId,
      video_current_time: event.currentTime || 0,
      video_elapsed_time: event.watchedSeconds,
      video_percent: event.percentageWatched,
      video_duration: event.duration,
      timestamp: event.timestamp,
    });
  }

  setUser(userId: string): void {
    this.ensureInitialized();
    
    window.gtag('set', 'user_id', userId);
  }
} 
